import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';


export default function IndividualBusiness() {
    const { id } = useParams();
    const [business, setBusiness] = useState(null);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [businessSubscription, setBusinessSubscription] = useState(null);
    const [pricingPlans, setPricingPlans] = useState([]);
    const [billingMode, setBillingMode] = useState('seats');
    const [seatsQuantity, setSeatsQuantity] = useState(1);
    const [selectedTierId, setSelectedTierId] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [collectionMethod, setCollectionMethod] = useState('charge_automatically');
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');
    const [latestInvoice, setLatestInvoice] = useState(null);
    const [allInvoices, setAllInvoices] = useState([]);
    const [showInvoicesModal, setShowInvoicesModal] = useState(false);
    const [adminLoading, setAdminLoading] = useState(null);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [addUserLoading, setAddUserLoading] = useState(null);
    const { user } = useAuth();

    useEffect(() => {
        fetchUserRole();
        fetchBusinessDetails();
    }, [id]);

    useEffect(() => {
        if (business?.idbusiness || business?.id || id) {
            fetchBusinessSubscription();
        }
    }, [business?.idbusiness, business?.id, id]);

    useEffect(() => {
        const b = business?.idbusiness ?? business?.id ?? id;
        if (!b || b === 'current') return;
        if (businessSubscription?.status === 'active' || businessSubscription?.stripeSubscription?.status === 'active') {
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/businesses/${b}/latest-invoice`, { withCredentials: true })
                .then(res => setLatestInvoice(res.data?.invoice ?? null))
                .catch(() => setLatestInvoice(null));
        } else {
            setLatestInvoice(null);
        }
    }, [business?.idbusiness, business?.id, id, businessSubscription?.status, businessSubscription?.stripeSubscription?.status]);

    useEffect(() => {
        fetchPricingPlans();
    }, []);

    const fetchPricingPlans = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                { withCredentials: true }
            );
            setPricingPlans(Array.isArray(res.data) ? res.data : res.data?.plans || []);
        } catch (e) {
            console.error('Error fetching pricing plans:', e);
        }
    };

    const fetchBusinessSubscription = async () => {
        const bid = business?.idbusiness ?? business?.id ?? id;
        if (!bid || bid === 'current') return;
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/subscription`,
                { withCredentials: true }
            );
            setBusinessSubscription(res.data.subscription ?? null);
        } catch (e) {
            console.error('Error fetching business subscription:', e);
            setBusinessSubscription(null);
        }
    };

    const bid = business?.idbusiness ?? business?.id ?? id;

    const setUserAdmin = async (userId, isAdmin) => {
        if (!bid || bid === 'current') return;
        setAdminLoading(userId);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/users/${userId}/admin`,
                { isAdmin },
                { withCredentials: true }
            );
            setUsers(prev => prev.map(u => u.idusers === userId ? { ...u, is_admin: isAdmin } : u));
        } catch (e) {
            console.error('Error setting business admin:', e);
        } finally {
            setAdminLoading(null);
        }
    };

    const handleCreateSubscription = async () => {
        if (!bid || bid === 'current' || !selectedTierId) {
            setSubscriptionError('Velg en prisplan.');
            return;
        }
        setSubscriptionLoading(true);
        setSubscriptionError('');
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/checkout-session`,
                {
                    tierId: parseInt(selectedTierId, 10),
                    quantity: Math.max(1, parseInt(seatsQuantity, 10) || 1),
                    billingMode: business?.billing_mode || billingMode,
                    discountCode: discountCode.trim() || undefined,
                    collectionMethod: collectionMethod,
                },
                { withCredentials: true }
            );
            if (res.data?.checkoutUrl) {
                // If directSubscription is true, subscription was created directly (send_invoice)
                // Otherwise, redirect to Stripe checkout
                if (res.data.directSubscription) {
                    // Refresh business details to show new subscription
                    await fetchBusinessDetails();
                    await fetchBusinessSubscription();
                    // Redirect to success page
                    window.location.href = res.data.checkoutUrl;
                } else {
                    // Redirect to Stripe checkout
                    window.location.href = res.data.checkoutUrl;
                }
            } else {
                setSubscriptionError('Kunne ikke opprette betalingslenke.');
                setSubscriptionLoading(false);
            }
        } catch (e) {
            setSubscriptionError(e.response?.data?.message || 'Kunne ikke starte abonnement.');
            setSubscriptionLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        if (!bid || bid === 'current') return;
        setSubscriptionLoading(true);
        setSubscriptionError('');
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/portal-session`,
                {},
                { withCredentials: true }
            );
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                setSubscriptionError('Kunne ikke åpne betalingsportalen.');
            }
        } catch (e) {
            setSubscriptionError(e.response?.data?.message || 'Kunne ikke åpne portalen.');
        }
        setSubscriptionLoading(false);
    };

    const handleBillingModeChange = async (mode) => {
        if (!bid || bid === 'current') return;
        setBillingMode(mode);
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/billing-mode`,
                { billingMode: mode },
                { withCredentials: true }
            );
        } catch (e) {
            console.error('Error updating billing mode:', e);
        }
    };

    const handleSearchUsers = async (query) => {
        setUserSearchQuery(query);
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        setSearchLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/users/search?q=${encodeURIComponent(query)}`,
                { withCredentials: true }
            );
            // Filter out users already in this business
            const existingUserIds = new Set(users.map(u => u.idusers));
            setSearchResults(res.data.filter(u => !existingUserIds.has(u.idusers)));
        } catch (e) {
            console.error('Error searching users:', e);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAddUserToBusiness = async (userId) => {
        if (!bid || bid === 'current') return;
        setAddUserLoading(userId);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/users`,
                { userId },
                { withCredentials: true }
            );
            // Refresh business details to get updated user list
            await fetchBusinessDetails();
            setShowAddUserModal(false);
            setUserSearchQuery('');
            setSearchResults([]);
        } catch (e) {
            console.error('Error adding user to business:', e);
            alert(e.response?.data?.message || 'Kunne ikke legge til bruker');
        } finally {
            setAddUserLoading(null);
        }
    };

    const fetchAllInvoices = async () => {
        if (!bid || bid === 'current') return;
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${bid}/invoices`,
                { withCredentials: true }
            );
            setAllInvoices(res.data?.invoices || []);
        } catch (e) {
            console.error('Error fetching invoices:', e);
            setAllInvoices([]);
        }
    };

    const fetchUserRole = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`,
                { withCredentials: true }
            );
            setUserRole(response.data.role);
        } catch (error) {
            console.error('Error fetching user role:', error);
        }
    };

    const fetchBusinessDetails = async () => {
        if (!id) return;
        try {
            const [businessResponse, usersResponse] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${id}`,
                    { withCredentials: true }
                ),
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/businesses/${id}/users`,
                    { withCredentials: true }
                )
            ]);

            if (businessResponse?.data) {
                const b = businessResponse.data;
                setBusiness(b);
                setUsers(usersResponse.data || []);
                if (b.billing_mode) setBillingMode(b.billing_mode);
                if (b.totalSeats != null) setSeatsQuantity(b.totalSeats);
                if (b.discount_code) setDiscountCode(b.discount_code);
            } else {
                setError('Ingen bedriftsdata funnet');
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching business:', error);
            setError('Kunne ikke laste bedriftsdetaljer. ' + (error.response?.data?.message || ''));
            setLoading(false);
        }
    };

    if (loading) return <div className="loading">Laster...</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!business) return <div className="error-message">Bedrift ikke funnet</div>;

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Dine bedriftsdetaljer</h1>
            </div>

            <div className="detail-card">
                <div className="detail-header">
                    <h2>{business.name}</h2>
                    <span className="seats-badge">
                        {business.seats_available} plasser tilgjengelig
                    </span>
                </div>
                
                <div className="detail-content">
                    <div className="detail-row">
                        <label>Bedriftskode:</label>
                        <span>{business.code}</span>
                    </div>

                    <div className="subscription-details">
                        <h3>Abonnementsinformasjon</h3>
                        <div className="detail-row">
                            <label>Plan:</label>
                            <span>{business.subscription_name || businessSubscription?.pricing_name || 'Ingen aktiv plan'}</span>
                        </div>
                        {business.subscription_price && (
                            <div className="detail-row">
                                <label>Pris:</label>
                                <span>NOK {business.subscription_price}</span>
                            </div>
                        )}
                        {businessSubscription?.pricing_price != null && (
                            <div className="detail-row">
                                <label>Pris:</label>
                                <span>NOK {businessSubscription.pricing_price}</span>
                            </div>
                        )}
                        {business.subscription_duration_months && (
                            <div className="detail-row">
                                <label>Varighet:</label>
                                <span>{business.subscription_duration_months} måneder</span>
                            </div>
                        )}
                        {businessSubscription?.stripeSubscription?.current_period_end && (
                            <div className="detail-row">
                                <label>Abonnement utløper:</label>
                                <span>{new Date(businessSubscription.stripeSubscription.current_period_end * 1000).toLocaleDateString('nb-NO', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                })}</span>
                            </div>
                        )}
                    </div>

                    {bid && bid !== 'current' && (
                        <div className="business-stripe-section">
                            <h3>Bedrift betaler for brukere</h3>
                            <p className="business-stripe-desc">Kun bedriften faktureres. Brukere som registrerer seg med bedriftskoden får tilgang uten å betale selv, og beholder kontoen uavhengig av bedriften.</p>
                            <div className="billing-mode-row">
                                <label>Fakturering:</label>
                                <div className="billing-mode-options">
                                    <label className="billing-option">
                                        <input
                                            type="radio"
                                            name="billingMode"
                                            checked={(business.billing_mode || billingMode) === 'seats'}
                                            onChange={() => handleBillingModeChange('seats')}
                                        />
                                        <span>Etter antall plasser (bookede seter)</span>
                                    </label>
                                    <label className="billing-option">
                                        <input
                                            type="radio"
                                            name="billingMode"
                                            checked={(business.billing_mode || billingMode) === 'usage'}
                                            onChange={() => handleBillingModeChange('usage')}
                                        />
                                        <span>Etter bruk (kun de som faktisk bruker appen)</span>
                                    </label>
                                </div>
                            </div>
                            {businessSubscription?.status === 'active' || businessSubscription?.stripeSubscription?.status === 'active' ? (
                                <div className="business-stripe-actions">
                                    <button type="button" className="btn-manage" onClick={handleManageSubscription} disabled={subscriptionLoading}>
                                        {subscriptionLoading ? 'Åpner...' : 'Administrer betaling / avbryt'}
                                    </button>
                                    {latestInvoice && (
                                        <div className="invoice-links">
                                            {latestInvoice.hostedInvoiceUrl && (
                                                <a href={latestInvoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="btn-invoice">
                                                    Åpne faktura (betal)
                                                </a>
                                            )}
                                            {latestInvoice.invoicePdf && (
                                                <a href={latestInvoice.invoicePdf} target="_blank" rel="noopener noreferrer" className="btn-invoice btn-invoice-pdf" download>
                                                    Last ned faktura (PDF)
                                                </a>
                                            )}
                                            <button 
                                                type="button" 
                                                className="btn-invoice btn-view-all"
                                                onClick={() => {
                                                    setShowInvoicesModal(true);
                                                    fetchAllInvoices();
                                                }}
                                            >
                                                Vis alle fakturaer
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="business-stripe-setup">
                                    <label>Velg prisplan (per plass)</label>
                                    <select
                                        value={selectedTierId}
                                        onChange={(e) => setSelectedTierId(e.target.value)}
                                        className="tier-select"
                                    >
                                        <option value="">Velg plan</option>
                                        {pricingPlans.filter(p => {
                                            if (!p.tiers_mode || !p.tiers || !p.tiers.length) return false;
                                            if (process.env.NODE_ENV === 'development') return true;
                                            // Only show "Full tilgang" and premium plans (plan name or tier name)
                                            const planName = (p.name || '').toLowerCase();
                                            const hasPremiumTier = (p.tiers || []).some(t => (t.name || '').toLowerCase().includes('premium'));
                                            return planName.includes('premium') || planName.includes('full tilgang') || hasPremiumTier;
                                        }).map(plan => {
                                            const tiers = (plan.tiers || []).slice().sort((a, b) => {
                                                const isYearly = t => t.payment_interval === 'year' && (t.interval_count === 1 || t.interval_count == null);
                                                if (isYearly(a) && !isYearly(b)) return -1;
                                                if (!isYearly(a) && isYearly(b)) return 1;
                                                return 0;
                                            });
                                            return tiers.map(tier => {
                                                const intervalLabel = tier.payment_interval === 'year' && (tier.interval_count === 1 || tier.interval_count == null)
                                                    ? ' / år'
                                                    : tier.payment_interval === 'month'
                                                        ? (tier.interval_count === 6 ? ' / 6 mnd' : ' / mnd')
                                                        : tier.payment_interval ? ` / ${tier.payment_interval}` : '';
                                                return (
                                                    <option key={tier.id} value={tier.id}>
                                                        {plan.name} – {tier.name} (NOK {tier.amount}){intervalLabel}
                                                    </option>
                                                );
                                            });
                                        }).flat()}
                                    </select>
                                    {(business.billing_mode || billingMode) === 'seats' && (
                                        <>
                                            <label>Antall plasser</label>
                                            <input
                                                type="number"
                                                min={1}
                                                value={seatsQuantity}
                                                onChange={(e) => setSeatsQuantity(e.target.value)}
                                                className="seats-input"
                                            />
                                            <p className="seats-help">Du kan starte med 1 plass. Antall plasser oppdateres automatisk i Stripe når flere brukere kobles til bedriften (via bedriftskode eller av admin).</p>
                                        </>
                                    )}
                                    <div className="discount-code-section">
                                        <label htmlFor="discountCode">Rabattkode (valgfritt)</label>
                                        <input
                                            id="discountCode"
                                            type="text"
                                            value={discountCode}
                                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                                            placeholder="Skriv inn rabattkode"
                                            className="discount-input"
                                        />
                                    </div>
                                    <div className="collection-method-section">
                                        <label htmlFor="collectionMethod">Betalingsmetode</label>
                                        <select
                                            id="collectionMethod"
                                            value={collectionMethod}
                                            onChange={(e) => setCollectionMethod(e.target.value)}
                                            className="collection-method-select"
                                        >
                                            <option value="charge_automatically">Automatisk betaling</option>
                                            <option value="send_invoice">Send faktura</option>
                                        </select>
                                        <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                                            {collectionMethod === 'charge_automatically' 
                                                ? 'Stripe vil automatisk belaste kunden ved periodens slutt'
                                                : 'Stripe vil sende faktura via e-post til kunden'}
                                        </small>
                                    </div>
                                    {subscriptionError && <p className="subscription-error">{subscriptionError}</p>}
                                    <button type="button" className="btn-create" onClick={handleCreateSubscription} disabled={subscriptionLoading || !selectedTierId}>
                                        {subscriptionLoading ? 'Videresender...' : 'Opprett abonnement (betaling via Stripe)'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="detail-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3>Bedriftsbrukere</h3>
                            {bid && bid !== 'current' && (userRole === 'superadmin' || user?.is_admin) && (
                                <button 
                                    type="button" 
                                    className="btn-add-user"
                                    onClick={() => setShowAddUserModal(true)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: 'var(--primary-color)',
                                        color: 'var(--primary-dark)',
                                        border: 'none',
                                        borderRadius: 'var(--border-radius-sm)',
                                        cursor: 'pointer',
                                        fontWeight: 500
                                    }}
                                >
                                    <i className="fas fa-plus"></i> Legg til bruker
                                </button>
                            )}
                        </div>
                        <div className="associated-users">
                            {users.length > 0 ? (
                                users.map(user => (
                                    <Link 
                                        to={`/users/${user.idusers}`} 
                                        key={user.idusers} 
                                        className="user-item"
                                    >
                                        <div className="user-info">
                                            <span className="user-name">
                                                {user.name}
                                                {user.is_admin && <span className="admin-badge">Admin</span>}
                                            </span>
                                            <span className="user-email">{user.email}</span>
                                            <span className="user-role">{user.role}</span>
                                        </div>
                                        <div className="user-actions">
                                            {user.is_admin ? (
                                                <button
                                                    type="button"
                                                    className="btn-remove-admin"
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUserAdmin(user.idusers, false); }}
                                                    disabled={adminLoading === user.idusers}
                                                    title="Fjern som bedriftsadmin"
                                                >
                                                    Fjern admin
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn-set-admin"
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUserAdmin(user.idusers, true); }}
                                                    disabled={adminLoading === user.idusers}
                                                    title="Sett som bedriftsadmin"
                                                >
                                                    Sett som admin
                                                </button>
                                            )}
                                            <Link to={`/users/${user.idusers}`} className="user-link" onClick={e => e.stopPropagation()}>
                                                <i className="fas fa-chevron-right"></i>
                                            </Link>
                                        </div>
                                    </Link>
                                ))
                            ) : (
                                <p>Ingen brukere funnet i din bedrift</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add User Modal */}
            {showAddUserModal && (
                <div className="modal-overlay" onClick={() => setShowAddUserModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Legg til bruker i bedrift</h2>
                            <button 
                                type="button" 
                                className="modal-close"
                                onClick={() => {
                                    setShowAddUserModal(false);
                                    setUserSearchQuery('');
                                    setSearchResults([]);
                                }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="userSearch">Søk etter bruker (navn eller e-post)</label>
                                <input
                                    type="text"
                                    id="userSearch"
                                    value={userSearchQuery}
                                    onChange={(e) => handleSearchUsers(e.target.value)}
                                    placeholder="Skriv minst 2 tegn for å søke..."
                                    className="form-input"
                                    autoFocus
                                />
                            </div>
                            {searchLoading && (
                                <div style={{ padding: '16px', textAlign: 'center' }}>
                                    <i className="fas fa-spinner fa-spin"></i> Søker...
                                </div>
                            )}
                            {!searchLoading && searchResults.length > 0 && (
                                <div className="search-results-user">
                                    {searchResults.map(user => (
                                        <div key={user.idusers} className="search-result-item">
                                            <div className="user-info">
                                                <span className="user-name">{user.name}</span>
                                                <span className="user-email">{user.email}</span>
                                                <span className="user-role">{user.role}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn-add"
                                                onClick={() => handleAddUserToBusiness(user.idusers)}
                                                disabled={addUserLoading === user.idusers}
                                            >
                                                {addUserLoading === user.idusers ? (
                                                    <i className="fas fa-spinner fa-spin"></i>
                                                ) : (
                                                    'Legg til'
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!searchLoading && userSearchQuery.length >= 2 && searchResults.length === 0 && (
                                <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                                    Ingen brukere funnet
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Invoices Modal */}
            {showInvoicesModal && (
                <div className="modal-overlay" onClick={() => setShowInvoicesModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <h2>Alle fakturaer</h2>
                            <button 
                                type="button" 
                                className="modal-close"
                                onClick={() => setShowInvoicesModal(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            {allInvoices.length > 0 ? (
                                <div className="invoices-list">
                                    {allInvoices.map(invoice => (
                                        <div key={invoice.id} className="invoice-item">
                                            <div className="invoice-info">
                                                <div className="invoice-header">
                                                    <span className="invoice-number">
                                                        {invoice.invoiceNumber || invoice.id}
                                                    </span>
                                                    <span className={`invoice-status invoice-status-${invoice.status}`}>
                                                        {invoice.status === 'paid' ? 'Betalt' : 
                                                         invoice.status === 'open' ? 'Åpen' :
                                                         invoice.status === 'draft' ? 'Utkast' :
                                                         invoice.status === 'void' ? 'Annullert' :
                                                         invoice.status}
                                                    </span>
                                                </div>
                                                <div className="invoice-details">
                                                    <span className="invoice-amount">
                                                        {(invoice.amountDue / 100).toFixed(2)} {invoice.currency?.toUpperCase()}
                                                    </span>
                                                    {invoice.dueDate && (
                                                        <span className="invoice-due-date">
                                                            Forfall: {new Date(invoice.dueDate * 1000).toLocaleDateString('nb-NO')}
                                                        </span>
                                                    )}
                                                    {invoice.created && (
                                                        <span className="invoice-date">
                                                            Opprettet: {new Date(invoice.created * 1000).toLocaleDateString('nb-NO')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="invoice-actions">
                                                {invoice.hostedInvoiceUrl && (
                                                    <a 
                                                        href={invoice.hostedInvoiceUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn-invoice btn-small"
                                                    >
                                                        Åpne
                                                    </a>
                                                )}
                                                {invoice.invoicePdf && (
                                                    <a 
                                                        href={invoice.invoicePdf} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn-invoice btn-invoice-pdf btn-small"
                                                        download
                                                    >
                                                        <i className="fas fa-download"></i> PDF
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ padding: '32px', textAlign: 'center', color: '#666' }}>
                                    Ingen fakturaer funnet
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .detail-card {
                    background: var(--white);
                    border-radius: var(--border-radius-md);
                    padding: var(--spacing-lg);
                    box-shadow: var(--shadow-sm);
                    margin-top: 20px;
                }

                .detail-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid var(--border-color);
                }

                .seats-badge {
                    background-color: var(--primary-color);
                    color: var(--primary-dark);
                    padding: 6px 12px;
                    border-radius: var(--border-radius-lg);
                    font-size: 14px;
                }

                .detail-row {
                    display: flex;
                    margin-bottom: 16px;
                }

                .detail-row label {
                    width: 150px;
                    font-weight: 500;
                    color: var(--text-muted);
                }

                .subscription-details {
                    margin: 24px 0;
                    padding: var(--spacing-md);
                    background: var(--bg-light);
                    border-radius: var(--border-radius-md);
                }

                .associated-users {
                    margin-top: 16px;
                }

                .user-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 16px;
                    border: 1px solid var(--border-color);
                    border-radius: var(--border-radius-sm);
                    margin-bottom: 8px;
                    text-decoration: none;
                    color: inherit;
                    transition: all 0.2s;
                }

                .user-item:hover {
                    background: var(--bg-light);
                    border-color: var(--border-color);
                }

                .user-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .user-name {
                    font-weight: 500;
                }

                .user-email {
                    color: var(--text-muted);
                    font-size: 14px;
                }

                .user-role {
                    font-size: 12px;
                    background: var(--bg-muted);
                    padding: 2px 8px;
                    border-radius: 12px;
                    color: var(--text-dark);
                }
                .admin-badge {
                    margin-left: 8px;
                    font-size: 11px;
                    background: var(--primary-color);
                    color: var(--primary-dark);
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-weight: 600;
                }
                .user-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .btn-set-admin, .btn-remove-admin {
                    font-size: 12px;
                    padding: 4px 10px;
                    border-radius: var(--border-radius-sm);
                    border: 1px solid var(--border-color);
                    background: var(--white);
                    color: var(--text-dark);
                    cursor: pointer;
                }
                .btn-set-admin:hover, .btn-remove-admin:hover:not(:disabled) {
                    background: var(--bg-light);
                }
                .btn-set-admin:disabled, .btn-remove-admin:disabled { opacity: 0.7; cursor: not-allowed; }
                .user-link { color: inherit; display: flex; align-items: center; }

                .business-stripe-section {
                    margin-top: var(--spacing-lg);
                    padding: 20px;
                    background: var(--primary-light);
                    border-radius: var(--border-radius-md);
                    border: 1px solid var(--primary-color);
                }
                .business-stripe-section h3 { margin-top: 0; color: var(--primary-dark); }
                .business-stripe-desc {
                    color: var(--text-muted);
                    font-size: 14px;
                    margin-bottom: var(--spacing-md);
                }
                .billing-mode-row { margin-bottom: var(--spacing-md); }
                .billing-mode-row > label { display: block; font-weight: 500; margin-bottom: var(--spacing-sm); color: var(--text-dark); }
                .billing-mode-options { display: flex; flex-direction: column; gap: var(--spacing-sm); }
                .billing-option { display: flex; align-items: center; gap: var(--spacing-sm); cursor: pointer; color: var(--text-dark); }
                .billing-option input { margin: 0; accent-color: var(--primary-color); }
                .business-stripe-actions { margin-top: var(--spacing-md); }
                .business-stripe-setup { display: flex; flex-direction: column; gap: 12px; max-width: 400px; margin-top: var(--spacing-md); }
                .business-stripe-setup label { font-weight: 500; color: var(--text-dark); }
                .tier-select, .seats-input, .discount-input { padding: 8px 12px; border: 1px solid var(--border-color); border-radius: var(--border-radius-sm); background: var(--white); color: var(--text-dark); }
                .seats-help { font-size: 13px; color: var(--text-muted); margin: 8px 0 0; max-width: 420px; }
                .discount-code-section { margin-top: var(--spacing-md); }
                .discount-code-section label { display: block; font-weight: 500; margin-bottom: var(--spacing-sm); color: var(--text-dark); }
                .discount-input { width: 100%; text-transform: uppercase; }
                .subscription-error { color: var(--accent-error); font-size: 14px; margin: 0; }
                .btn-manage, .btn-create {
                    padding: 10px 16px;
                    border-radius: var(--border-radius-sm);
                    border: none;
                    cursor: pointer;
                    font-size: 14px;
                    align-self: flex-start;
                }
                .btn-manage { background: var(--bg-dark); color: var(--text-light); }
                .btn-manage:hover:not(:disabled) { background: var(--primary-dark); }
                .btn-create { background: var(--primary-color); color: var(--primary-dark); }
                .btn-create:hover:not(:disabled) { background: var(--primary-dark); color: var(--text-light); }
                .btn-manage:disabled, .btn-create:disabled { opacity: 0.7; cursor: not-allowed; }
                .invoice-links { display: flex; flex-wrap: wrap; gap: var(--spacing-sm); margin-top: var(--spacing-sm); align-items: center; }
                .btn-invoice { padding: 8px 14px; border-radius: var(--border-radius-sm); font-size: 13px; text-decoration: none; background: var(--primary-color); color: var(--primary-dark); border: none; cursor: pointer; }
                .btn-invoice:hover { background: var(--primary-dark); color: var(--text-light); }
                .btn-invoice-pdf { background: var(--bg-muted); color: var(--text-dark); }
                .btn-invoice-pdf:hover { background: var(--border-color); }
                .btn-view-all { background: var(--bg-dark); color: var(--text-light); }
                .btn-view-all:hover { background: var(--primary-dark); }

                .invoices-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .invoice-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px;
                    border: 1px solid var(--border-color);
                    border-radius: var(--border-radius-sm);
                    background: var(--white);
                }

                .invoice-info {
                    flex: 1;
                }

                .invoice-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }

                .invoice-number {
                    font-weight: 600;
                    font-size: 16px;
                }

                .invoice-status {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .invoice-status-paid {
                    background: #d4edda;
                    color: #155724;
                }

                .invoice-status-open {
                    background: #fff3cd;
                    color: #856404;
                }

                .invoice-status-draft {
                    background: #e2e3e5;
                    color: #383d41;
                }

                .invoice-status-void {
                    background: #f8d7da;
                    color: #721c24;
                }

                .invoice-details {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    color: var(--text-muted);
                    font-size: 14px;
                }

                .invoice-amount {
                    font-weight: 600;
                    color: var(--text-dark);
                    font-size: 16px;
                }

                .invoice-actions {
                    display: flex;
                    gap: 8px;
                }

                .btn-small {
                    padding: 6px 12px;
                    font-size: 13px;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding-top: 60px;
                    z-index: 1000;
                    overflow-y: auto;
                }

                .modal-content {
                    background: var(--white);
                    border-radius: var(--border-radius-md);
                    width: 90%;
                    max-width: 600px;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
                    margin-bottom: 40px;
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-md);
                    border-bottom: 1px solid var(--border-color);
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 1.5rem;
                }

                .modal-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: var(--text-muted);
                    padding: 0;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .modal-close:hover {
                    color: var(--text-dark);
                }

                .modal-body {
                    padding: var(--spacing-md);
                    overflow-y: auto;
                }

                .search-results-user {
                    margin-top: 16px;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .search-result-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px;
                    border: 1px solid var(--border-color);
                    border-radius: var(--border-radius-sm);
                    margin-bottom: 8px;
                }

                .search-result-item .user-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    flex: 1;
                }

                .btn-add {
                    padding: 8px 16px;
                    background-color: var(--primary-color);
                    color: var(--primary-dark);
                    border: none;
                    border-radius: var(--border-radius-sm);
                    cursor: pointer;
                    font-weight: 500;
                }

                .btn-add:hover:not(:disabled) {
                    opacity: 0.9;
                }

                .btn-add:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .form-input {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid var(--border-color);
                    border-radius: var(--border-radius-sm);
                    font-size: 14px;
                }
            `}</style>
        </div>
    );
} 